<template>
  <div class="jump-link-selector">
    <el-select
      v-model="localConfig.type"
      placeholder="请选择跳转类型"
      style="width: 100%"
      :disabled="disabled"
      @change="handleTypeChange"
    >
      <el-option v-for="item in jumpTypeOptions" :key="item.value" :label="item.label" :value="item.value" />
    </el-select>

    <div v-if="localConfig.type === JUMP_TYPE_COMPETITION" class="jump-link-block">
      <el-select
        v-model="localConfig.competitionId"
        filterable
        remote
        reserve-keyword
        clearable
        placeholder="搜索赛事名称"
        :remote-method="fetchCompetitions"
        :loading="competitionLoading"
        style="width: 100%"
        :disabled="disabled"
        @visible-change="(visible) => handleRemoteDropdownVisible(visible, 'competitionQuery', fetchCompetitions)"
        @clear="() => handleRemoteClear('competitionQuery', fetchCompetitions)"
        @change="handleCompetitionChange"
      >
        <el-option
          v-for="item in competitionOptions"
          :key="item.id"
          :label="item.title"
          :value="String(item.id)"
        />
      </el-select>
    </div>

    <div v-else-if="localConfig.type === JUMP_TYPE_ACTIVITY_PARTICIPATION" class="jump-link-block">
      <el-select
        v-model="localConfig.activityParticipationId"
        filterable
        remote
        reserve-keyword
        clearable
        placeholder="搜索活动名称"
        :remote-method="fetchActivityParticipations"
        :loading="activityParticipationLoading"
        style="width: 100%"
        :disabled="disabled"
        @visible-change="(visible) => handleRemoteDropdownVisible(visible, 'activityParticipationQuery', fetchActivityParticipations)"
        @clear="() => handleRemoteClear('activityParticipationQuery', fetchActivityParticipations)"
        @change="handleActivityParticipationChange"
      >
        <el-option
          v-for="item in activityParticipationOptions"
          :key="item.id"
          :label="item.title"
          :value="String(item.id)"
        />
      </el-select>
    </div>

    <div v-else-if="localConfig.type === JUMP_TYPE_ARTIST" class="jump-link-block">
      <el-select
        v-model="localConfig.artistId"
        filterable
        remote
        reserve-keyword
        clearable
        placeholder="搜索艺人账户"
        :remote-method="fetchArtists"
        :loading="artistLoading"
        style="width: 100%; margin-bottom: 12px"
        :disabled="disabled"
        @visible-change="(visible) => handleRemoteDropdownVisible(visible, 'artistQuery', fetchArtists)"
        @clear="() => handleRemoteClear('artistQuery', fetchArtists)"
        @change="handleArtistChange"
      >
        <el-option
          v-for="item in artistOptions"
          :key="item.artistId"
          :label="formatArtistLabel(item)"
          :value="String(item.artistId)"
        />
      </el-select>
      <el-radio-group v-model="localConfig.artistPage" :disabled="disabled" @change="emitChange">
        <el-radio v-for="item in artistPageOptions" :key="item.value" :label="item.value">
          {{ item.label }}
        </el-radio>
      </el-radio-group>
    </div>

    <div v-else-if="localConfig.type === JUMP_TYPE_NOTICE" class="jump-link-block">
      <el-select
        v-model="localConfig.noticeId"
        filterable
        remote
        reserve-keyword
        clearable
        placeholder="搜索通告标题或编号"
        :remote-method="fetchNotices"
        :loading="noticeLoading"
        style="width: 100%"
        :disabled="disabled"
        @visible-change="(visible) => handleRemoteDropdownVisible(visible, 'noticeQuery', fetchNotices)"
        @clear="() => handleRemoteClear('noticeQuery', fetchNotices)"
        @change="handleNoticeChange"
      >
        <el-option
          v-for="item in noticeOptions"
          :key="item.id"
          :label="item.subtitle ? `${item.name} (${item.subtitle})` : item.name"
          :value="String(item.id)"
        />
      </el-select>
    </div>

    <div v-else-if="localConfig.type === JUMP_TYPE_TALENT" class="jump-link-block">
      <el-radio-group
        v-model="localConfig.talentMode"
        :disabled="disabled"
        style="margin-bottom: 12px"
        @change="handleTalentModeChange"
      >
        <el-radio v-for="item in talentModeOptions" :key="item.value" :label="item.value">
          {{ item.label }}
        </el-radio>
      </el-radio-group>

      <el-select
        v-if="localConfig.talentMode === TALENT_MODE_TAXONOMY"
        v-model="localConfig.taxonomyId"
        filterable
        remote
        reserve-keyword
        clearable
        placeholder="搜索才艺分类"
        :remote-method="fetchTaxonomies"
        :loading="taxonomyLoading"
        style="width: 100%; margin-bottom: 12px"
        :disabled="disabled"
        @visible-change="(visible) => handleRemoteDropdownVisible(visible, 'taxonomyQuery', fetchTaxonomies)"
        @clear="() => handleRemoteClear('taxonomyQuery', fetchTaxonomies)"
        @change="handleTaxonomyChange"
      >
        <el-option
          v-for="item in taxonomyOptions"
          :key="item.id"
          :label="item.name"
          :value="String(item.id)"
        />
      </el-select>

      <el-select
        v-else
        v-model="localConfig.artistUserId"
        filterable
        remote
        reserve-keyword
        clearable
        placeholder="搜索艺人账户"
        :remote-method="fetchTalentArtists"
        :loading="talentArtistLoading"
        style="width: 100%; margin-bottom: 12px"
        :disabled="disabled"
        @visible-change="(visible) => handleRemoteDropdownVisible(visible, 'talentArtistQuery', fetchTalentArtists)"
        @clear="() => handleRemoteClear('talentArtistQuery', fetchTalentArtists)"
        @change="handleTalentArtistChange"
      >
        <el-option
          v-for="item in talentArtistOptions"
          :key="item.userId"
          :label="formatArtistLabel(item)"
          :value="String(item.userId)"
        />
      </el-select>

      <div
        class="talent-video-picker-trigger"
        :class="{ disabled: disabled || !canSelectTalentVideo, selected: !!localConfig.videoId }"
        @click="openVideoPicker"
      >
        <template v-if="selectedVideo">
          <img
            v-if="selectedVideo.coverUrl"
            class="trigger-cover"
            :src="resolveMediaUrl(selectedVideo.coverUrl)"
            alt=""
          >
          <div class="trigger-info">
            <div class="trigger-title">{{ selectedVideo.title || '未命名视频' }}</div>
            <div class="trigger-meta">
              <span>{{ selectedVideo.artistName || '艺人' }}</span>
              <span class="trigger-taxonomy">{{ selectedVideo.taxonomyName || localConfig.taxonomyName || '-' }}</span>
            </div>
          </div>
        </template>
        <span v-else class="trigger-placeholder">选择才艺视频</span>
        <div class="trigger-actions">
          <i
            v-if="localConfig.videoId && !disabled"
            class="el-icon-circle-close trigger-clear"
            @click.stop="clearSelectedVideo"
          />
          <i class="el-icon-arrow-right" />
        </div>
      </div>

      <TalentVideoPickerDialog
        :visible.sync="videoPickerVisible"
        :selected-id="localConfig.videoId"
        :taxonomy-id="localConfig.talentMode === TALENT_MODE_TAXONOMY ? localConfig.taxonomyId : ''"
        :artist-user-id="localConfig.talentMode === TALENT_MODE_USER ? localConfig.artistUserId : ''"
        @select="handleVideoPick"
      />

      <div v-if="selectedVideo" class="video-preview-row">
        <img
          v-if="selectedVideo.coverUrl"
          class="video-cover"
          :src="resolveMediaUrl(selectedVideo.coverUrl)"
          alt="cover"
        >
        <el-button type="text" :disabled="!selectedVideo.videoUrl" @click="previewVisible = true">
          预览视频
        </el-button>
      </div>
    </div>

    <div v-else-if="localConfig.type === JUMP_TYPE_MALL" class="jump-link-block">
      <el-select
        v-model="localConfig.parentCategoryId"
        filterable
        clearable
        placeholder="选择商城一级分类"
        style="width: 100%; margin-bottom: 12px"
        :disabled="disabled"
        @change="handleMallParentCategoryChange"
      >
        <el-option
          v-for="item in mallLevel1Options"
          :key="item.id"
          :label="item.categoryName"
          :value="String(item.id)"
        />
      </el-select>
      <el-select
        v-model="localConfig.categoryId"
        filterable
        clearable
        placeholder="选择商城二级分类"
        style="width: 100%; margin-bottom: 12px"
        :disabled="disabled || !localConfig.parentCategoryId"
        @change="handleMallSubCategoryChange"
      >
        <el-option
          v-for="item in mallLevel2Options"
          :key="item.id"
          :label="item.categoryName"
          :value="String(item.id)"
        />
      </el-select>
      <div
        class="mall-product-picker-trigger"
        :class="{ disabled: disabled || !localConfig.categoryId, selected: !!localConfig.productId }"
        @click="openProductPicker"
      >
        <template v-if="selectedProduct">
          <img
            v-if="selectedProduct.coverUrl"
            class="trigger-cover"
            :src="resolveMediaUrl(selectedProduct.coverUrl)"
            alt=""
          >
          <div class="trigger-info">
            <div class="trigger-title">{{ selectedProduct.title || '未命名商品' }}</div>
            <div class="trigger-meta">
              <span>{{ formatProductPrice(selectedProduct.price) }}</span>
              <span class="trigger-taxonomy">{{ selectedProduct.categoryName || localConfig.categoryName || '-' }}</span>
            </div>
          </div>
        </template>
        <span v-else class="trigger-placeholder">选择商品</span>
        <div class="trigger-actions">
          <i
            v-if="localConfig.productId && !disabled"
            class="el-icon-circle-close trigger-clear"
            @click.stop="clearSelectedProduct"
          />
          <i class="el-icon-arrow-right" />
        </div>
      </div>

      <MallProductPickerDialog
        :visible.sync="productPickerVisible"
        :selected-id="localConfig.productId"
        :category-id="localConfig.categoryId"
        @select="handleProductPick"
      />
    </div>

    <div v-else-if="localConfig.type === JUMP_TYPE_CUSTOM" class="jump-link-block">
      <el-input
        v-model="localConfig.customUrl"
        placeholder="请输入小程序路径或 http(s) 链接"
        :disabled="disabled"
        @input="emitChange"
      />
    </div>

    <div v-if="previewLabel" class="jump-link-summary">
      {{ previewLabel }}
    </div>

    <el-dialog title="视频预览" :visible.sync="previewVisible" width="640px" append-to-body>
      <video
        v-if="selectedVideo && selectedVideo.videoUrl"
        class="preview-video"
        :src="resolveMediaUrl(selectedVideo.videoUrl)"
        :poster="resolveMediaUrl(selectedVideo.coverUrl)"
        controls
        autoplay
      />
    </el-dialog>
  </div>
</template>

<script>
import { getCompetitionEvents } from '../../api/competition'
import { getActivityParticipationEvents } from '../../api/activityParticipation'
import { getArtistUsers, getPlatformTalentVideoArtistOptions } from '../../api/platformManage'
import { getNoticeTargets } from '../../api/opsResourceTarget'
import { getOpsTaxonomies } from '../../api/opsTaxonomy'
import { getMallCategories } from '../../api/mall'
import { resolveMediaUrl } from '../../utils/format'
import TalentVideoPickerDialog from './TalentVideoPickerDialog.vue'
import MallProductPickerDialog from './MallProductPickerDialog.vue'
import {
  ARTIST_PAGE_OPTIONS,
  ARTIST_PAGE_PROFILE,
  JUMP_TYPE_OPTIONS,
  TALENT_MODE_OPTIONS,
  buildJumpLinkResult,
  createEmptyJumpConfig,
  inferJumpConfigFromValue,
  resolveMallCategorySelection,
  JUMP_TYPE_ARTIST,
  JUMP_TYPE_COMPETITION,
  JUMP_TYPE_ACTIVITY_PARTICIPATION,
  JUMP_TYPE_CUSTOM,
  JUMP_TYPE_MALL,
  JUMP_TYPE_NOTICE,
  JUMP_TYPE_TALENT,
  TALENT_MODE_TAXONOMY,
  TALENT_MODE_USER
} from '../../utils/jumpLink'

const REMOTE_DEFAULT_LIMIT = 10

export default {
  name: 'JumpLinkSelector',
  components: {
    TalentVideoPickerDialog,
    MallProductPickerDialog
  },
  props: {
    value: {
      type: Object,
      default: () => ({ jumpUrl: '', jumpConfig: null })
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      JUMP_TYPE_COMPETITION,
      JUMP_TYPE_ACTIVITY_PARTICIPATION,
      JUMP_TYPE_ARTIST,
      JUMP_TYPE_NOTICE,
      JUMP_TYPE_TALENT,
      JUMP_TYPE_MALL,
      JUMP_TYPE_CUSTOM,
      TALENT_MODE_TAXONOMY,
      TALENT_MODE_USER,
      jumpTypeOptions: JUMP_TYPE_OPTIONS,
      artistPageOptions: ARTIST_PAGE_OPTIONS,
      talentModeOptions: TALENT_MODE_OPTIONS,
      localConfig: createEmptyJumpConfig(),
      syncing: false,
      competitionOptions: [],
      competitionLoading: false,
      competitionQuery: '',
      activityParticipationOptions: [],
      activityParticipationLoading: false,
      activityParticipationQuery: '',
      artistOptions: [],
      artistLoading: false,
      artistQuery: '',
      noticeOptions: [],
      noticeLoading: false,
      noticeQuery: '',
      taxonomyOptions: [],
      taxonomyLoading: false,
      taxonomyQuery: '',
      talentArtistOptions: [],
      talentArtistLoading: false,
      talentArtistQuery: '',
      selectedVideoData: null,
      videoPickerVisible: false,
      mallCategoryTree: [],
      selectedProductData: null,
      productPickerVisible: false,
      previewVisible: false
    }
  },
  computed: {
    previewLabel() {
      return buildJumpLinkResult(this.localConfig).jumpLabel
    },
    canSelectTalentVideo() {
      if (this.localConfig.talentMode === TALENT_MODE_USER) {
        return !!this.localConfig.artistUserId
      }
      return !!this.localConfig.taxonomyId
    },
    selectedVideo() {
      if (!this.localConfig.videoId) return null
      if (this.selectedVideoData && String(this.selectedVideoData.id) === String(this.localConfig.videoId)) {
        return this.selectedVideoData
      }
      return {
        id: this.localConfig.videoId,
        title: this.localConfig.videoTitle,
        coverUrl: this.localConfig.videoCoverUrl || '',
        videoUrl: this.localConfig.videoPlayUrl || '',
        artistName: this.localConfig.artistName || '',
        taxonomyName: this.localConfig.taxonomyName || ''
      }
    },
    mallLevel1Options() {
      return (this.mallCategoryTree || []).filter((item) => Number(item.level) === 1 && Number(item.status) === 1)
    },
    mallLevel2Options() {
      if (!this.localConfig.parentCategoryId) return []
      const parent = (this.mallCategoryTree || []).find(
        (item) => String(item.id) === String(this.localConfig.parentCategoryId)
      )
      return ((parent && parent.children) || []).filter(
        (item) => Number(item.level) === 2 && Number(item.status) === 1
      )
    },
    selectedProduct() {
      if (!this.localConfig.productId) return null
      if (this.selectedProductData && String(this.selectedProductData.id) === String(this.localConfig.productId)) {
        return this.selectedProductData
      }
      return {
        id: this.localConfig.productId,
        title: this.localConfig.productTitle,
        coverUrl: this.localConfig.productCoverUrl || '',
        categoryName: this.localConfig.categoryName || ''
      }
    }
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(value) {
        this.syncFromValue(value)
      }
    }
  },
  created() {
    this.loadMallCategories()
  },
  methods: {
    resolveMediaUrl,
    syncFromValue(value) {
      this.syncing = true
      this.localConfig = inferJumpConfigFromValue(value || {})
      this.localConfig = resolveMallCategorySelection(this.mallCategoryTree, this.localConfig)
      this.seedSelectedOptions()
      this.$nextTick(() => {
        this.syncing = false
      })
    },
    seedSelectedOptions() {
      const config = this.localConfig
      if (config.competitionId && config.competitionTitle) {
        this.competitionOptions = [{ id: config.competitionId, title: config.competitionTitle }]
      }
      if (config.activityParticipationId && config.activityParticipationTitle) {
        this.activityParticipationOptions = [{
          id: config.activityParticipationId,
          title: config.activityParticipationTitle
        }]
      }
      if (config.artistId && config.artistName) {
        this.artistOptions = [{ artistId: config.artistId, artistName: config.artistName, nickname: config.artistName }]
      }
      if (config.noticeId && config.noticeTitle) {
        this.noticeOptions = [{ id: config.noticeId, name: config.noticeTitle, subtitle: '' }]
      }
      if (config.taxonomyId && config.taxonomyName) {
        this.taxonomyOptions = [{ id: config.taxonomyId, name: config.taxonomyName }]
      }
      if (config.artistUserId && config.artistName) {
        this.talentArtistOptions = [{ userId: config.artistUserId, artistName: config.artistName, nickname: config.artistName }]
      }
      if (config.videoId && config.videoTitle) {
        this.selectedVideoData = {
          id: config.videoId,
          title: config.videoTitle,
          coverUrl: config.videoCoverUrl || '',
          videoUrl: config.videoPlayUrl || '',
          artistName: config.artistName || '',
          taxonomyName: config.taxonomyName || ''
        }
      } else {
        this.selectedVideoData = null
      }
      if (config.productId && config.productTitle) {
        this.selectedProductData = {
          id: config.productId,
          title: config.productTitle,
          coverUrl: config.productCoverUrl || '',
          categoryName: config.categoryName || '',
          price: config.productPrice || ''
        }
      } else {
        this.selectedProductData = null
      }
    },
    emitChange() {
      if (this.syncing) return
      const result = buildJumpLinkResult(this.localConfig)
      this.$emit('input', result)
      this.$emit('change', result)
    },
    handleTypeChange() {
      const type = this.localConfig.type
      this.localConfig = {
        ...createEmptyJumpConfig(type),
        type
      }
      if (type === JUMP_TYPE_MALL) {
        this.loadMallCategories()
      } else if (type === JUMP_TYPE_COMPETITION) {
        this.fetchCompetitions('')
      } else if (type === JUMP_TYPE_ACTIVITY_PARTICIPATION) {
        this.fetchActivityParticipations('')
      } else if (type === JUMP_TYPE_ARTIST) {
        this.fetchArtists('')
      } else if (type === JUMP_TYPE_NOTICE) {
        this.fetchNotices('')
      } else if (type === JUMP_TYPE_TALENT) {
        if (this.localConfig.talentMode === TALENT_MODE_TAXONOMY) {
          this.fetchTaxonomies('')
        } else {
          this.fetchTalentArtists('')
        }
      }
      this.emitChange()
    },
    formatArtistLabel(item) {
      const name = item.artistName || item.nickname || '未命名艺人'
      const mobile = item.mobile ? ` / ${item.mobile}` : ''
      return `${name}${mobile}`
    },
    openVideoPicker() {
      if (this.disabled || !this.canSelectTalentVideo) return
      this.videoPickerVisible = true
    },
    clearSelectedVideo() {
      this.localConfig.videoId = ''
      this.localConfig.videoTitle = ''
      this.localConfig.videoCoverUrl = ''
      this.localConfig.videoPlayUrl = ''
      this.selectedVideoData = null
      this.emitChange()
    },
    handleVideoPick(item) {
      if (!item) return
      this.localConfig.videoId = String(item.id)
      this.localConfig.videoTitle = item.title || ''
      this.localConfig.videoCoverUrl = item.coverUrl || ''
      this.localConfig.videoPlayUrl = item.videoUrl || ''
      this.selectedVideoData = {
        id: item.id,
        title: item.title || '',
        coverUrl: item.coverUrl || '',
        videoUrl: item.videoUrl || '',
        artistName: item.artistName || '',
        taxonomyName: item.taxonomyName || ''
      }
      this.emitChange()
    },
    normalizeRemoteKeyword(keyword) {
      return (keyword || '').trim()
    },
    limitRemoteOptions(items, keyword) {
      const list = Array.isArray(items) ? items : []
      return keyword ? list : list.slice(0, REMOTE_DEFAULT_LIMIT)
    },
    prependSelectedOption(items, selectedId, selectedOption, idKey = 'id') {
      if (!selectedId || !selectedOption) return items
      const exists = items.some((item) => String(item[idKey]) === String(selectedId))
      return exists ? items : [selectedOption, ...items]
    },
    handleRemoteDropdownVisible(visible, queryKey, fetchMethod) {
      if (visible) {
        fetchMethod.call(this, this[queryKey])
      }
    },
    handleRemoteClear(queryKey, fetchMethod) {
      this[queryKey] = ''
      fetchMethod.call(this, '')
    },
    async fetchCompetitions(keyword = '') {
      this.competitionQuery = this.normalizeRemoteKeyword(keyword)
      this.competitionLoading = true
      try {
        const list = await getCompetitionEvents({ keyword: this.competitionQuery, status: 2 })
        let items = this.limitRemoteOptions(list, this.competitionQuery)
        items = this.prependSelectedOption(
          items,
          this.localConfig.competitionId,
          this.localConfig.competitionTitle
            ? { id: this.localConfig.competitionId, title: this.localConfig.competitionTitle }
            : null
        )
        this.competitionOptions = items
      } finally {
        this.competitionLoading = false
      }
    },
    handleCompetitionChange(value) {
      const target = this.competitionOptions.find((item) => String(item.id) === String(value))
      this.localConfig.competitionTitle = target ? target.title : ''
      this.emitChange()
    },
    async fetchActivityParticipations(keyword = '') {
      this.activityParticipationQuery = this.normalizeRemoteKeyword(keyword)
      this.activityParticipationLoading = true
      try {
        const list = await getActivityParticipationEvents({
          keyword: this.activityParticipationQuery,
          status: 2
        })
        let items = this.limitRemoteOptions(list, this.activityParticipationQuery)
        items = this.prependSelectedOption(
          items,
          this.localConfig.activityParticipationId,
          this.localConfig.activityParticipationTitle
            ? {
              id: this.localConfig.activityParticipationId,
              title: this.localConfig.activityParticipationTitle
            }
            : null
        )
        this.activityParticipationOptions = items
      } finally {
        this.activityParticipationLoading = false
      }
    },
    handleActivityParticipationChange(value) {
      const target = this.activityParticipationOptions.find((item) => String(item.id) === String(value))
      this.localConfig.activityParticipationTitle = target ? target.title : ''
      this.emitChange()
    },
    async fetchArtists(keyword = '') {
      this.artistQuery = this.normalizeRemoteKeyword(keyword)
      this.artistLoading = true
      try {
        const data = await getArtistUsers({
          keyword: this.artistQuery,
          page: 1,
          pageSize: this.artistQuery ? 20 : REMOTE_DEFAULT_LIMIT
        })
        let items = this.limitRemoteOptions((data && data.records) || [], this.artistQuery)
        items = this.prependSelectedOption(
          items,
          this.localConfig.artistId,
          this.localConfig.artistName
            ? {
              artistId: this.localConfig.artistId,
              artistName: this.localConfig.artistName,
              nickname: this.localConfig.artistName
            }
            : null,
          'artistId'
        )
        this.artistOptions = items
      } finally {
        this.artistLoading = false
      }
    },
    handleArtistChange(value) {
      const target = this.artistOptions.find((item) => String(item.artistId) === String(value))
      this.localConfig.artistName = target ? (target.artistName || target.nickname || '') : ''
      if (!this.localConfig.artistPage) {
        this.localConfig.artistPage = ARTIST_PAGE_PROFILE
      }
      this.emitChange()
    },
    async fetchNotices(keyword = '') {
      this.noticeQuery = this.normalizeRemoteKeyword(keyword)
      this.noticeLoading = true
      try {
        const list = await getNoticeTargets({ keyword: this.noticeQuery })
        let items = this.limitRemoteOptions(list, this.noticeQuery)
        items = this.prependSelectedOption(
          items,
          this.localConfig.noticeId,
          this.localConfig.noticeTitle
            ? { id: this.localConfig.noticeId, name: this.localConfig.noticeTitle, subtitle: '' }
            : null
        )
        this.noticeOptions = items
      } finally {
        this.noticeLoading = false
      }
    },
    handleNoticeChange(value) {
      const target = this.noticeOptions.find((item) => String(item.id) === String(value))
      this.localConfig.noticeTitle = target ? target.name : ''
      this.emitChange()
    },
    async fetchTaxonomies(keyword = '') {
      this.taxonomyQuery = this.normalizeRemoteKeyword(keyword)
      this.taxonomyLoading = true
      try {
        const list = await getOpsTaxonomies({
          businessType: 7,
          taxonomyType: 1,
          keyword: this.taxonomyQuery,
          status: 1
        })
        let items = (Array.isArray(list) ? list : []).map((item) => ({
          id: String(item.id),
          name: item.taxonomyName
        }))
        items = this.limitRemoteOptions(items, this.taxonomyQuery)
        items = this.prependSelectedOption(
          items,
          this.localConfig.taxonomyId,
          this.localConfig.taxonomyName
            ? { id: String(this.localConfig.taxonomyId), name: this.localConfig.taxonomyName }
            : null
        )
        this.taxonomyOptions = items
      } finally {
        this.taxonomyLoading = false
      }
    },
    handleTaxonomyChange(value) {
      const target = this.taxonomyOptions.find((item) => String(item.id) === String(value))
      this.localConfig.taxonomyName = target ? target.name : ''
      this.localConfig.videoId = ''
      this.localConfig.videoTitle = ''
      this.localConfig.videoCoverUrl = ''
      this.localConfig.videoPlayUrl = ''
      this.selectedVideoData = null
      this.emitChange()
    },
    async fetchTalentArtists(keyword = '') {
      this.talentArtistQuery = this.normalizeRemoteKeyword(keyword)
      this.talentArtistLoading = true
      try {
        const data = await getPlatformTalentVideoArtistOptions({
          keyword: this.talentArtistQuery,
          page: 1,
          pageSize: this.talentArtistQuery ? 20 : REMOTE_DEFAULT_LIMIT
        })
        let items = this.limitRemoteOptions((data && data.records) || [], this.talentArtistQuery)
        items = this.prependSelectedOption(
          items,
          this.localConfig.artistUserId,
          this.localConfig.artistName
            ? {
              userId: this.localConfig.artistUserId,
              artistName: this.localConfig.artistName,
              nickname: this.localConfig.artistName
            }
            : null,
          'userId'
        )
        this.talentArtistOptions = items
      } finally {
        this.talentArtistLoading = false
      }
    },
    handleTalentArtistChange(value) {
      const target = this.talentArtistOptions.find((item) => String(item.userId) === String(value))
      this.localConfig.artistName = target ? (target.artistName || target.nickname || '') : ''
      this.localConfig.videoId = ''
      this.localConfig.videoTitle = ''
      this.localConfig.videoCoverUrl = ''
      this.localConfig.videoPlayUrl = ''
      this.selectedVideoData = null
      this.emitChange()
    },
    handleTalentModeChange() {
      this.localConfig.taxonomyId = ''
      this.localConfig.taxonomyName = ''
      this.localConfig.artistUserId = ''
      this.localConfig.artistName = ''
      this.localConfig.videoId = ''
      this.localConfig.videoTitle = ''
      this.localConfig.videoCoverUrl = ''
      this.localConfig.videoPlayUrl = ''
      this.selectedVideoData = null
      this.taxonomyQuery = ''
      this.talentArtistQuery = ''
      this.emitChange()
      if (this.localConfig.talentMode === TALENT_MODE_TAXONOMY) {
        this.fetchTaxonomies('')
      } else {
        this.fetchTalentArtists('')
      }
    },
    async loadMallCategories() {
      const tree = await getMallCategories({})
      this.mallCategoryTree = Array.isArray(tree) ? tree : []
      this.localConfig = resolveMallCategorySelection(this.mallCategoryTree, this.localConfig)
    },
    handleMallParentCategoryChange(value) {
      const target = this.mallLevel1Options.find((item) => String(item.id) === String(value))
      this.localConfig.parentCategoryName = target ? target.categoryName : ''
      this.localConfig.categoryId = ''
      this.localConfig.categoryName = ''
      this.localConfig.productId = ''
      this.localConfig.productTitle = ''
      this.localConfig.productCoverUrl = ''
      this.selectedProductData = null
      this.emitChange()
    },
    handleMallSubCategoryChange(value) {
      const target = this.mallLevel2Options.find((item) => String(item.id) === String(value))
      this.localConfig.categoryName = target ? target.categoryName : ''
      this.localConfig.productId = ''
      this.localConfig.productTitle = ''
      this.localConfig.productCoverUrl = ''
      this.selectedProductData = null
      this.emitChange()
    },
    openProductPicker() {
      if (this.disabled || !this.localConfig.categoryId) return
      this.productPickerVisible = true
    },
    clearSelectedProduct() {
      this.localConfig.productId = ''
      this.localConfig.productTitle = ''
      this.localConfig.productCoverUrl = ''
      this.selectedProductData = null
      this.emitChange()
    },
    formatProductPrice(value) {
      if (value === undefined || value === null || value === '') return '¥0.00'
      const amount = Number(value)
      if (Number.isNaN(amount)) return `¥${value}`
      return `¥${amount.toFixed(2)}`
    },
    handleProductPick(item) {
      if (!item) return
      this.localConfig.productId = String(item.id)
      this.localConfig.productTitle = item.title || ''
      this.localConfig.productCoverUrl = item.coverUrl || ''
      this.selectedProductData = {
        id: item.id,
        title: item.title || '',
        coverUrl: item.coverUrl || '',
        categoryName: item.categoryName || this.localConfig.categoryName || '',
        price: item.price
      }
      this.emitChange()
    }
  }
}
</script>

<style scoped>
.jump-link-selector {
  width: 100%;
}

.jump-link-block {
  margin-top: 12px;
}

.jump-link-summary {
  margin-top: 10px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #f4f8ff;
  color: #5b6b88;
  font-size: 13px;
  line-height: 1.5;
}

.video-preview-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
}

.video-cover {
  width: 96px;
  height: 54px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #ebeef5;
}

.preview-video {
  display: block;
  width: 100%;
  max-height: 360px;
  background: #000;
}

.talent-video-picker-trigger,
.mall-product-picker-trigger {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 56px;
  padding: 10px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.2s ease;
}

.talent-video-picker-trigger:hover:not(.disabled),
.mall-product-picker-trigger:hover:not(.disabled) {
  border-color: #409eff;
}

.talent-video-picker-trigger.disabled,
.mall-product-picker-trigger.disabled {
  background: #f5f7fa;
  color: #c0c4cc;
  cursor: not-allowed;
}

.trigger-cover {
  width: 72px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
  flex-shrink: 0;
}

.trigger-info {
  flex: 1;
  min-width: 0;
}

.trigger-title {
  font-size: 14px;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.trigger-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}

.trigger-taxonomy {
  color: #409eff;
  flex-shrink: 0;
}

.trigger-placeholder {
  flex: 1;
  color: #c0c4cc;
  font-size: 14px;
}

.trigger-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #c0c4cc;
  flex-shrink: 0;
}

.trigger-clear {
  font-size: 16px;
}

.trigger-clear:hover {
  color: #909399;
}
</style>
